import { useMemo } from "react";
import { useLocation } from "react-router";
import { readerStart } from "@/auth/auto-start";
import { ReaderEntryButton } from "@/auth/reader-entry-button";
import { LibraryShell } from "@/components/design-switcher";
import { type SessionStore, sessionStore, useSession } from "@/convex/session";
import {
  BookmarkList,
  selectBookmarksNewestFirst,
} from "@/library/bookmark-list";
import { libraryBooks } from "@/library/books";
import { DailyRecommendationsSection } from "@/library/daily-recommendations";
import { useBookmarks } from "@/library/use-bookmarks";
import { useDailyRecommendations } from "@/library/use-daily-recommendations";
import type { BookmarkValue } from "@/storage/db";

export const CONTINUE_READING_LIMIT = 3;

function ContinueReading({
  bookmarks,
}: {
  bookmarks: readonly BookmarkValue[];
}) {
  if (selectBookmarksNewestFirst(bookmarks, "in-progress").length === 0)
    return null;
  return (
    <section className="bg-stone-50 px-6 py-10">
      <div className="mx-auto max-w-3xl">
        <h2 className="text-foreground text-2xl font-semibold tracking-tight">
          Continue reading
        </h2>
        <p className="text-muted-foreground mt-1">Pick up where you left off</p>
        <div className="mt-6">
          <BookmarkList
            bookmarks={bookmarks}
            filter="in-progress"
            limit={CONTINUE_READING_LIMIT}
          />
        </div>
      </div>
    </section>
  );
}

// RequireReader starts a reader on its own, so a signed-out library means
// that attempt failed. The retry is the same start, gate included, as a
// plain link or form that works without scripts.
function RetryStart() {
  const { entry } = readerStart(useLocation());
  return (
    <section className="bg-stone-50 px-6 py-6 text-center">
      <p className="text-foreground/90 mb-3">
        We couldn&apos;t start a reader.
      </p>
      <ReaderEntryButton entry={entry}>Try again</ReaderEntryButton>
    </section>
  );
}

export function LibraryPage({
  store = sessionStore,
}: {
  store?: SessionStore;
}) {
  const session = useSession(store);
  const bookmarks = useBookmarks();
  const books = useMemo(() => libraryBooks(bookmarks), [bookmarks]);
  const recommendations = useDailyRecommendations();

  return (
    <>
      {session.status === "signed-out" && <RetryStart />}
      <ContinueReading bookmarks={bookmarks} />
      <DailyRecommendationsSection {...recommendations} />
      <LibraryShell books={books} />
    </>
  );
}
